/**
 * CONSAI Business App - Admin Reports Management
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Settings, RefreshCw, Loader2,
  CheckCircle2, XCircle, Clock, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReports = () => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('reports');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ step1_instructions: '', step2_instructions: '' });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [regenerating, setRegenerating] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      params.append('page', page);
      params.append('per_page', 20);
      
      const res = await fetch(`${API_URL}/api/admin/reports/?${params}`, {
        credentials: 'include'
      });
      
      if (res.status === 401) {
        navigate('/admin');
        return;
      }
      
      const data = await res.json();
      setReports(data.reports);
      setTotal(data.total);
    } catch (error) {
      toast.error('Error loading reports');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, page, navigate]);

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/settings`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setSettings({
          step1_instructions: data.step1_instructions || '',
          step2_instructions: data.step2_instructions || '',
        });
      }
    } catch (error) {
      console.error('Error loading settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchSettings();
  }, [fetchReports]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Settings saved');
      } else {
        toast.error('Error saving settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRegenerate = async (reportId) => {
    setRegenerating(reportId);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/${reportId}/regenerate`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Report regeneration started');
        fetchReports();
      } else {
        toast.error('Error regenerating report');
      }
    } catch (error) {
      toast.error('Error regenerating report');
    } finally {
      setRegenerating(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="h-3 w-3 mr-1" />Processing</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#021945]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/dashboard')}
            data-testid="admin-reports-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold font-manrope text-slate-900">
            Report Management
          </h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Tab Buttons */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'reports' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('reports')}
            className=""
            data-testid="tab-reports"
          >
            <FileText className="h-4 w-4 mr-2" />
            All Reports
          </Button>
          <Button
            variant={activeTab === 'settings' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('settings')}
            className=""
            data-testid="tab-settings"
          >
            <Settings className="h-4 w-4 mr-2" />
            AI Settings
          </Button>
        </div>

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <>
            {/* Status Filter */}
            <div className="mb-6 flex gap-2 items-center flex-wrap">
              {[
                { value: '', label: 'All' },
                { value: 'completed', label: 'Completed' },
                { value: 'processing', label: 'Processing' },
                { value: 'failed', label: 'Failed' },
              ].map((f) => (
                <Button
                  key={f.value}
                  variant={filterStatus === f.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setFilterStatus(f.value); setPage(1); }}
                  className=""
                  data-testid={`filter-${f.value || 'all'}`}
                >
                  {f.label}
                </Button>
              ))}
              <span className="text-sm text-slate-500 ml-2">
                Total: {total} reports
              </span>
            </div>

            {/* Reports List */}
            <div className="space-y-4" data-testid="reports-list">
              {reports.map((report) => (
                <Card key={report.report_id} data-testid={`report-card-${report.report_id}`}>
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold font-manrope">
                            {report.business_name || report.user_name || 'Unknown Business'}
                          </h3>
                          {getStatusBadge(report.generation_status)}
                        </div>
                        <div className="text-sm text-slate-500 space-y-1">
                          <p>User: {report.user_email || 'N/A'}</p>
                          <p>Created: {new Date(report.created_at).toLocaleString()}</p>
                          {report.generated_at && (
                            <p>Generated: {new Date(report.generated_at).toLocaleString()}</p>
                          )}
                          {report.error_message && (
                            <p className="text-red-500">Error: {report.error_message}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {report.generation_status === 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedReport(report); setShowReportDialog(true); }}
                            data-testid={`view-report-${report.report_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRegenerate(report.report_id)}
                          disabled={regenerating === report.report_id}
                          data-testid={`regenerate-report-${report.report_id}`}
                        >
                          {regenerating === report.report_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Regenerate
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {reports.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No reports found</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Pagination */}
            {total > 20 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm">
                  Page {page} of {Math.ceil(total / 20)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= Math.ceil(total / 20)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle className="font-manrope">AI Report Generation Settings</CardTitle>
              <CardDescription className="">
                Customize the prompts used for AI-generated questions and reports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {settingsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label className="">Step 1: Question Generation Instructions</Label>
                    <Textarea
                      value={settings.step1_instructions}
                      onChange={(e) => setSettings({ ...settings, step1_instructions: e.target.value })}
                      placeholder="Custom instructions for generating follow-up questions (optional)"
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500">
                      Use placeholders: {'{{business_type}}'}, {'{{location}}'}, {'{{revenue_range}}'}, {'{{foundation_answers}}'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="">Step 2: Report Generation Instructions</Label>
                    <Textarea
                      value={settings.step2_instructions}
                      onChange={(e) => setSettings({ ...settings, step2_instructions: e.target.value })}
                      placeholder="Custom instructions for generating the final report (optional)"
                      rows={6}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500">
                      Use placeholders: {'{{business_type}}'}, {'{{business_name}}'}, {'{{location}}'}, {'{{revenue_range}}'}, {'{{foundation_answers}}'}, {'{{generated_answers}}'}
                    </p>
                  </div>

                  <Button
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                    className="bg-[#021945]"
                    data-testid="save-settings-btn"
                  >
                    {savingSettings && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Save Settings
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Report View Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              Report: {selectedReport?.business_name || 'Business Report'}
            </DialogTitle>
            <DialogDescription className="">
              Generated: {selectedReport?.generated_at ? new Date(selectedReport.generated_at).toLocaleString() : 'N/A'}
            </DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap">
            {selectedReport?.full_report_text || 'No content available'}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReports;
